import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { collection, doc, setDoc, getDoc, query, where, limit, getDocs, serverTimestamp, updateDoc, runTransaction, getDocs as getDocsQuery } from 'firebase/firestore';
import Layout from '../../components/ui/Layout';
import NoIndexHelmet from '../../components/seo/NoIndexHelmet';
import GctSummaryBreakdown from '../../components/tax/GctSummaryBreakdown';
import { Container, Card, Button, Spinner, Alert } from 'react-bootstrap';
import NotificationPermission from '../../components/ui/NotificationPermission';
import { isIOSOrMobileSafari } from '../../utils/browserUtils';
import { useAuth } from '../../components/context/AuthContext';
import { CART_STATUS, StatusHelpers } from '../../constants';
import { createLogger } from '../../utils/logger';
import { getPublicProductsSubscriptionAmount } from '../../config/publicProductsSubscription';
import { PUBLIC_PRODUCTS_SUBSCRIPTION_PURPOSE } from '../../services/publicProductsSubscriptionPayment';
import {
  parseQueryParams,
  parseWipayDataQueryParam,
} from '../../services/payment/parseWipayCallback';
import {
  getAccessibleCartDocs,
  findCartInstanceInCartDocs,
  findLegacyCartInUsersByOrderId,
  resolveCheckoutSnapshotForOrder,
  shouldFinalizeSuccessForCart,
  applySupersededOrderFulfillmentToCart,
  tryClaimCartCheckoutFulfillment,
  shouldRunInventoryDecrementForOrder,
  shouldSkipFulfillmentAfterFailedClaim,
} from '../../services/payment/resolveCartInstance';
import { buildMinimalCallbackTransaction } from '../../services/payment/buildMinimalCallbackTransaction';
import { WIPAY_CALLBACK_PROCESSING_TIMEOUT_MS } from '../../services/payment/wipayTimeouts';
import { promiseWithTimeout } from '../../utils/promiseWithTimeout';
import { shouldFulfillSupersededOrderPayment } from '../../services/payment/checkoutOrderGuard';
import {
  isEmployeeNotificationEnabled,
  notifyEmployeesNewOrder,
  resolveCustomerNameForNotification,
} from '../../services/payment/notifyEmployees';
import {
  buildDecrementSuccessUpdate,
  buildPartialDecrementFailureUpdate,
  cartItemsRemainingForDecrement,
} from '../../services/payment/paymentInventory';
import jsPDF from 'jspdf';
import { resolvePersistedGctSummary } from '../../utils/pricing/gctTax';

const logger = createLogger('WipayResponsePage');

export default function WipayResponsePage() {
  useAuth();
  const [saving, setSaving] = useState(true);
  const [error, setError] = useState(null);
  const [params, setParams] = useState({});
  const [cartTransactionId, setCartTransactionId] = useState(null);
  const [transactionData, setTransactionData] = useState(null);
  const [cartRestored, setCartRestored] = useState(false);
  const [cartStatus, setCartStatus] = useState(null);
  const [invalidCartStatus, setInvalidCartStatus] = useState(false);
  const [callbackTimedOut, setCallbackTimedOut] = useState(false);
  const [backgroundProcessing, setBackgroundProcessing] = useState(false);
  const navigate = useNavigate();
  
  const confirmationFee = parseFloat(process.env.REACT_APP_CONFIRMATION_FEE) || 500;
  const paymentType = process.env.REACT_APP_PAYMENT_TYPE || 'wipay';
  const paymentGateway = process.env.REACT_APP_PAYMENT_GATEWAY || 'WiPay';
  
  // Feature flag for employee notifications (customer notifications are handled in EmployeePackOrderPage)
  const enableEmployeeNotifications = isEmployeeNotificationEnabled();
  const paymentMethod = process.env.REACT_APP_PAYMENT_METHOD || 'Credit Card';

  // Function to generate and download receipt PDF
  const generateReceipt = () => {
    if (!transactionData) {
      logger.warn('Cannot generate receipt: transaction data not available');
      return;
    }

    // Determine if this is a full payment order (declare once at function scope)
    const isFullPaymentOrder = transactionData.paymentType === 'full' || params.payment_type === 'full';

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    let yPosition = margin;
    const footerSpaceNeeded = 50;

    const ensureSpaceForFooter = () => {
      if (yPosition + footerSpaceNeeded > pageHeight) {
        doc.addPage();
        yPosition = margin;
      }
    };

    // Helper function to add text with line wrapping
    const addText = (text, fontSize = 10, isBold = false, align = 'left') => {
      doc.setFontSize(fontSize);
      if (isBold) {
        doc.setFont(undefined, 'bold');
      } else {
        doc.setFont(undefined, 'normal');
      }
      
      const textWidth = pageWidth - (margin * 2);
      const lines = doc.splitTextToSize(text, textWidth);
      
      lines.forEach(line => {
        if (align === 'center') {
          doc.text(line, pageWidth / 2, yPosition, { align: 'center' });
        } else if (align === 'right') {
          doc.text(line, pageWidth - margin, yPosition, { align: 'right' });
        } else {
          doc.text(line, margin, yPosition);
        }
        yPosition += fontSize * 0.4;
      });
    };

    // Helper function to add line separator
    const addLine = () => {
      yPosition += 5;
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;
    };

    // Header
    addText('PACK MY CART', 18, true, 'center');
    yPosition += 5;
    addText('Receipt', 14, true, 'center');
    yPosition += 10;

    // Order Information
    addText('ORDER INFORMATION', 12, true);
    yPosition += 5;
    addText(`Order ID: ${transactionData.cartInstanceId || params.order_id}`, 10);
    addText(`Transaction ID: ${transactionData.paymentGatewayTransactionId || params.transaction_id || 'N/A'}`, 10);
    addText(`Date: ${new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}`, 10);
    addText(`Status: ${transactionData.status || params.status}`, 10);
    yPosition += 5;

    // Customer Information
    addText('CUSTOMER', 12, true);
    yPosition += 5;
    addText(transactionData.customerName || 'Unknown Customer', 10);
    yPosition += 5;

    // Supermarket Information
    addText('SUPERMARKET', 12, true);
    yPosition += 5;
    addText(transactionData.supermarketName || 'Unknown Supermarket', 10);
    yPosition += 5;

    // Payment Information
    addText('PAYMENT INFORMATION', 12, true);
    yPosition += 5;
    if (isFullPaymentOrder) {
      addText('*** FULL PAYMENT ORDER ***', 14, true, 'center');
      yPosition += 5;
      addText('(Confirmation fee collected as processing fee)', 10, false, 'center');
      yPosition += 5;
    }
    addText(`Payment Gateway: ${transactionData.paymentGateway || paymentGateway}`, 10);
    addText(`Payment Method: ${transactionData.paymentMethod || params.method || paymentMethod}`, 10);
    addText(`Currency: ${params.currency || 'JMD'}`, 10);
    addText(`Payment Type: ${isFullPaymentOrder ? 'Full Payment' : 'Confirmation Fee Only'}`, 10, true);
    yPosition += 5;

    // Delivery Information (if applicable)
    if (transactionData.deliveryOption && transactionData.deliveryOption !== 'pickup') {
      addText('DELIVERY INFORMATION', 12, true);
      yPosition += 5;
      addText(`Delivery Option: ${transactionData.deliveryOption}`, 10);
      if (transactionData.deliveryAddress) {
        addText(`Address: ${transactionData.deliveryAddress}`, 10);
      }
      if (transactionData.deliveryPhone) {
        addText(`Phone: ${transactionData.deliveryPhone}`, 10);
      }
      const deliveryFeeAmount = Number(transactionData.deliveryFee) || 0;
      if (deliveryFeeAmount > 0) {
        addText(`Delivery Fee: $${deliveryFeeAmount.toFixed(2)}`, 10);
      }
      if (transactionData.maxDeliveryDistance) {
        addText(`Max Delivery Distance: ${transactionData.maxDeliveryDistance} km`, 10);
      }
      yPosition += 5;
    }

    // Items (if available)
    let itemsTotalBeforeDeals = 0;
    if (transactionData.cartItems && transactionData.cartItems.length > 0) {
      addText('ITEMS', 12, true);
      yPosition += 5;
      
      transactionData.cartItems.forEach(item => {
        const itemTotal = (Number(item.price) || 0) * (Number(item.quantity) || 0);
        itemsTotalBeforeDeals += itemTotal;
        addText(`${item.name || 'Item'} x${item.quantity || 0}`, 10);
        addText(`$${Number(itemTotal).toFixed(2)}`, 10, false, 'right');
        yPosition += 3;
      });
      yPosition += 5;
    }

    addLine();

    // Totals
    const orderSubtotal = Number(transactionData.orderSubtotal) || 0;
    const totalDealSavings = itemsTotalBeforeDeals - orderSubtotal;
    const confirmationFeeAmount = Number(transactionData.confirmationFee) || confirmationFee;
    const deliveryFeeAmount = Number(transactionData.deliveryFee) || 0;
    const isDeliveryOrder = transactionData.deliveryOption === 'delivery';
    const taxSummary = resolvePersistedGctSummary(transactionData);
    
    if (totalDealSavings > 0.01) {
      addText(`Subtotal (before deals): $${itemsTotalBeforeDeals.toFixed(2)}`, 10, false, 'right');
      addText(`Deal Applied: -$${totalDealSavings.toFixed(2)}`, 10, false, 'right');
      yPosition += 2;
    }
    addText(`Subtotal: $${orderSubtotal.toFixed(2)}`, 10, false, 'right');
    if (taxSummary) {
      addText(`GCT Included: $${Number(taxSummary.gctIncludedTotal ?? taxSummary.totals?.gctIncluded ?? 0).toFixed(2)}`, 10, false, 'right');
    }
    if (deliveryFeeAmount > 0) {
      addText(`Delivery Fee: $${deliveryFeeAmount.toFixed(2)}`, 10, false, 'right');
    }
    if (isFullPaymentOrder) {
      addText(`Processing Fee (Confirmation Fee): $${confirmationFeeAmount.toFixed(2)}`, 10, false, 'right');
    } else {
      addText(`Confirmation Fee: $${confirmationFeeAmount.toFixed(2)}`, 10, false, 'right');
      addText(`Subtotal (Due ${isDeliveryOrder ? 'on Delivery' : 'at Pickup'}): $${orderSubtotal.toFixed(2)}`, 10, false, 'right');
    }
    addLine();
    
    // Calculate total paid correctly based on payment type
    const totalPaid = isFullPaymentOrder 
      ? (orderSubtotal + confirmationFeeAmount + deliveryFeeAmount)
      : (Number(transactionData.total) || Number(transactionData.amountPaid) || (confirmationFeeAmount + deliveryFeeAmount));
    
    addText(`Total Paid: $${Number(totalPaid).toFixed(2)}`, 12, true, 'right');
    if (isFullPaymentOrder) {
      yPosition += 3;
      addText('*** FULL AMOUNT PAID ***', 12, true, 'center');
      yPosition += 3;
      addText(`No amount due ${isDeliveryOrder ? 'on delivery' : 'at pickup'}`, 10, false, 'center');
    } else {
      yPosition += 3;
      addText(`Amount Due ${isDeliveryOrder ? 'on Delivery' : 'at Pickup'}: $${orderSubtotal.toFixed(2)}`, 10, false, 'right');
    }

    // Footer - ensure it fits on the page, add new page if needed
    ensureSpaceForFooter();
    yPosition += 20;
    addLine();
    addText('Thank you for your business!', 10, true, 'center');
    addText('For support, contact us at packmycart.help@gmail.com', 8, false, 'center');

    // Generate filename and download
    const filename = `receipt_${transactionData.cartInstanceId || params.order_id}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
    
    logger.debug('Receipt PDF generated and downloaded:', filename);
  };

  /**
   * Atomically claim inventory decrement for this transaction (idempotent across refreshes).
   * @returns {Promise<boolean>} true when this caller should run decrementInventory
   */
  const tryClaimInventoryDecrement = async (transactionRef) => {
    try {
      return await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(transactionRef);
        if (snap.exists() && snap.data()?.inventoryDecremented === true) {
          return false;
        }
        transaction.set(
          transactionRef,
          { inventoryDecremented: true, updatedAt: serverTimestamp() },
          { merge: true }
        );
        return true;
      });
    } catch (error) {
      logger.error('[WipayResponsePage] Failed to claim inventory decrement:', error);
      return false;
    }
  };

  const adjustInventoryQuantity = async (supermarketId, productId, quantityDelta) => {
    const inventoryRef = collection(db, 'Inventory');
    const inventoryQuery = query(
      inventoryRef,
      where('supermarket_id', '==', supermarketId),
      where('product_id', '==', productId),
      limit(1)
    );
    const snapshot = await getDocsQuery(inventoryQuery);
    if (snapshot.empty) {
      return null;
    }

    const inventoryDoc = snapshot.docs[0];
    const data = inventoryDoc.data();
    const oldQty = data.quantity || 0;
    const newQty = oldQty + quantityDelta;

    if (newQty < 0) {
      return { inventoryDoc, oldQty, newQty, applied: false };
    }

    await updateDoc(inventoryDoc.ref, { quantity: newQty });
    return { inventoryDoc, oldQty, newQty, applied: true };
  };

  /** @returns {Promise<{ allSucceeded: boolean, decrementedItems: Array<{ productId: string, quantity: number }> }>} */
  const decrementInventory = async (supermarketId, cartItems) => {
    logger.debug('Starting inventory decrement for supermarket', { supermarketId, cartItems });

    if (!Array.isArray(cartItems) || cartItems.length === 0) {
      return { allSucceeded: false, decrementedItems: [] };
    }

    let allSucceeded = true;
    const decrementedItems = [];

    for (const product of cartItems) {
      try {
        const quantity = product.quantity || 0;
        const result = await adjustInventoryQuantity(
          supermarketId,
          product.productId,
          -quantity
        );

        if (!result) {
          logger.warn(
            `[WipayResponsePage] No inventory document found for product ${product.productId} in supermarket ${supermarketId}`
          );
          allSucceeded = false;
          continue;
        }

        if (!result.applied) {
          logger.warn(
            `[WipayResponsePage] Cannot update inventory for product ${product.productId}: would result in negative quantity (${result.newQty})`
          );
          allSucceeded = false;
          continue;
        }

        decrementedItems.push({ productId: product.productId, quantity });
        logger.debug(
          `[WipayResponsePage] Updated inventory for product ${product.productId}: ${result.oldQty} -> ${result.newQty}`
        );
      } catch (error) {
        logger.error(`[WipayResponsePage] Error updating inventory for product ${product.productId}:`, error);
        allSucceeded = false;
      }
    }

    return { allSucceeded, decrementedItems };
  };

  // Function to increment inventory for failed payments (restore stock)
  // Note: Currently not used as simulated payments only decrement on success,
  // and real WiPay payments only decrement after success confirmation
  // Keeping for potential future use in edge cases
  /*
  const incrementInventory = async (supermarketId, cartItems) => {
    logger.debug('[WipayResponsePage] Starting inventory restoration for failed payment - supermarket:', supermarketId, 'with items:', cartItems);
    
    // Increment inventory for each product in the cart
    for (const product of cartItems) {
      try {
        // Use supermarket_id and product_id to fetch the Inventory document
        const inventoryRef = collection(db, 'Inventory');
        const inventoryQuery = query(
          inventoryRef,
          where('supermarket_id', '==', supermarketId),
          where('product_id', '==', product.productId),
          limit(1)
        );

        const snapshot = await getDocsQuery(inventoryQuery);
        
        if (!snapshot.empty) {
          const inventoryDoc = snapshot.docs[0];
          const data = inventoryDoc.data();
          
          const oldQty = data.quantity || 0;
          const newQty = oldQty + (product.quantity || 0);

          await updateDoc(inventoryDoc.ref, {
            quantity: newQty
          });
          logger.debug(`[WipayResponsePage] Restored inventory for product ${product.productId}: ${oldQty} -> ${newQty}`);
        } else {
          logger.warn(`[WipayResponsePage] No inventory document found for product ${product.productId} in supermarket ${supermarketId} - cannot restore`);
        }
      } catch (error) {
        logger.error(`[WipayResponsePage] Error restoring inventory for product ${product.productId}:`, error);
        // Don't throw the error - inventory restoration is important but shouldn't block error handling
      }
    }
  };
  */

  useEffect(() => {
    const q = parseQueryParams(window.location.search);
    setParams(q);

    const previewTransaction = buildMinimalCallbackTransaction(q, {
      confirmationFee,
      paymentGateway,
      paymentMethod,
    });
    setTransactionData(previewTransaction);
    setSaving(false);
    setBackgroundProcessing(true);
    
    // Debug logging for WiPay response
    logger.debug('[WipayResponsePage] Full WiPay response params:', q);
    logger.debug('[WipayResponsePage] Payment status:', q.status);
    logger.debug('[WipayResponsePage] Order ID:', q.order_id);
    logger.debug('[WipayResponsePage] Transaction ID:', q.transaction_id);
    logger.debug('[WipayResponsePage] Data parameter:', q.data);
    
    const processPaymentCallback = async () => {
                const responseCheckoutData = parseWipayDataQueryParam(q.data);
                const subscriptionPayload = responseCheckoutData;
                if (subscriptionPayload?.payment_purpose === PUBLIC_PRODUCTS_SUBSCRIPTION_PURPOSE) {
                  const docId = q.order_id || `sub_${Date.now()}`;
                  await setDoc(doc(db, 'WipayPayments', docId), {
                    ...q,
                    received_at: new Date(),
                    payment_kind: 'public_products_subscription',
                  });

                  if (q.status !== 'success') {
                    setError('Your subscription payment was not completed. You can try again from the products page.');
                    setSaving(false);
                    return;
                  }

                  const configuredAmount = getPublicProductsSubscriptionAmount();
                  const paid = parseFloat(q.total);
                  if (
                    configuredAmount > 0 &&
                    (!Number.isFinite(paid) || Math.abs(paid - configuredAmount) > 0.02)
                  ) {
                    logger.warn('[WipayResponsePage] Subscription amount mismatch:', { paid, configuredAmount });
                    setError(
                      'The paid amount does not match the current subscription price. If money was taken, please contact support.'
                    );
                    setSaving(false);
                    return;
                  }

                  const firebaseUser = auth.currentUser;
                  if (!firebaseUser) {
                    setError(
                      'Subscription payment succeeded, but you are not signed in. Sign in with the same account you used to pay, then open this page again from your browser history, or contact support.'
                    );
                    setSaving(false);
                    return;
                  }

                  const userDocId = subscriptionPayload.userDocId;
                  const payloadUserId = subscriptionPayload.userId;
                  if (!userDocId || payloadUserId !== firebaseUser.uid) {
                    setError('We could not match this payment to your account. Please contact support.');
                    setSaving(false);
                    return;
                  }

                  const userRef = doc(db, 'Users', userDocId);
                  const userSnap = await getDoc(userRef);
                  if (!userSnap.exists()) {
                    setError('User profile not found. Please contact support.');
                    setSaving(false);
                    return;
                  }
                  const profileIdentity = userSnap.data().identity_id;
                  if (profileIdentity !== firebaseUser.uid) {
                    setError('This payment does not belong to the signed-in account.');
                    setSaving(false);
                    return;
                  }

                  await updateDoc(userRef, {
                    products_subscription_active: true,
                    products_subscription_paid_at: serverTimestamp(),
                    products_subscription_wipay_transaction_id: q.transaction_id || null,
                    products_subscription_amount: Number.isFinite(paid) ? paid : configuredAmount,
                    products_subscription_currency: q.currency || subscriptionPayload.currency || 'JMD',
                  });

                  setSaving(false);
                  navigate('/products', { replace: true });
                  return;
                }

                const docId = q.order_id || undefined;
                
                const wipayPaymentData = {
                  ...q,
                  received_at: new Date(),
                };
                
                await setDoc(doc(db, 'WipayPayments', docId), wipayPaymentData);

            // Try to fetch the cart document by order_id
            let foundTransactionId = null;
            const cartDoc = await getDoc(doc(db, 'CartInstances', q.order_id));
            if (cartDoc.exists()) {
              foundTransactionId = cartDoc.data().transaction_id || null;
            } else {
              const cartInstancesRef = collection(db, 'CartInstances');
              const cartQuery = query(
                cartInstancesRef,
                where('order_id', '==', q.order_id),
                limit(1)
              );
              const snapshot = await getDocs(cartQuery);
              if (!snapshot.empty) {
                foundTransactionId = snapshot.docs[0].data().transaction_id || null;
              }
            }
            setCartTransactionId(foundTransactionId);

            // Check cart status before processing payment
            let currentCartStatus = null;
            if (q.order_id) {
              try {
                logger.debug('[WipayResponsePage] Checking cart status for order_id:', q.order_id);
                const cartDocs = await getAccessibleCartDocs({
                  checkoutData: responseCheckoutData,
                  fallbackUserId: q.user_id,
                });
                const cartMatch = findCartInstanceInCartDocs(cartDocs, q.order_id, responseCheckoutData);
                if (cartMatch) {
                  currentCartStatus = cartMatch.cartInstance.status;
                  logger.debug('[WipayResponsePage] Found cart status:', currentCartStatus);
                  setCartStatus(currentCartStatus);

                  // Check if cart status is invalid for payment processing
                  // Only block if payment was NOT successful AND cart has invalid status
                  if (q.status !== 'success' && StatusHelpers.isInvalidForPayment(currentCartStatus)) {
                    logger.debug('[WipayResponsePage] Cart has invalid status for failed payment:', currentCartStatus);
                    setInvalidCartStatus(true);
                    setError(`This cart has an invalid status for payment processing. Current status: ${StatusHelpers.getDisplayText(currentCartStatus)}`);
                    setSaving(false);
                    return;
                  }

                  // If payment was successful and cart is awaiting_packing, that's expected
                  if (q.status === 'success' && currentCartStatus === CART_STATUS.AWAITING_PACKING) {
                    logger.debug('[WipayResponsePage] Payment successful and cart already in awaiting_packing status - this is expected after refresh');
                    // Don't block the success page, just log the status
                  }

                  // If payment was successful but cart is in more advanced status, log warning but allow success page
                  if (q.status === 'success' && StatusHelpers.isMoreAdvancedThanAwaitingPacking(currentCartStatus)) {
                    logger.warn('[WipayResponsePage] Payment successful but cart is in more advanced status:', currentCartStatus, '- showing success page with warning');
                    // Don't block the success page, but this indicates a potential data integrity issue
                  }
                }
              } catch (statusError) {
                logger.error('[WipayResponsePage] Error checking cart status:', statusError);
                // Continue with payment processing if status check fails
              }
            }

                        // Create or update transaction document with all necessary fields
            if (q.status === 'success' && q.order_id) {
              const transactionRef = doc(db, 'Transactions', q.order_id);
              
                             // Check if this is a simulated payment
               if (paymentType === 'simulated') {
                 // logger.debug('Creating simulated transaction for order:', q.order_id);
                 
                 // For simulated payments, try to get the actual checkout data first
                 let checkoutData = responseCheckoutData;
                 let cartInstanceData = null;
                 
                 // Try to parse checkout data from WiPay response
                 try {
                   if (q.data && Object.keys(checkoutData).length === 0) {
                     checkoutData = JSON.parse(q.data);
                     // logger.debug('Parsed checkout data for simulated payment:', checkoutData);
                   }
                 } catch (error) {
                   logger.error('Failed to parse checkout data for simulated payment:', error);
                 }
                 
                                   // If checkout payload is incomplete, resolve cart instance from Firestore
                  let cartDocs = null;
                  let resolvedSimulatedCartMatch = null;
                  const needsSimulatedCartLookup =
                    !checkoutData.cartItems?.length ||
                    !checkoutData.cartInstanceId ||
                    (!checkoutData.supermarketId && !checkoutData.supermarket_id);
                  if (needsSimulatedCartLookup) {
                    try {
                      cartDocs = await getAccessibleCartDocs({
                        checkoutData,
                        fallbackUserId: q.user_id,
                      });
                      resolvedSimulatedCartMatch = findCartInstanceInCartDocs(cartDocs, q.order_id, checkoutData);
                      if (resolvedSimulatedCartMatch) {
                        cartInstanceData = resolvedSimulatedCartMatch.cartInstance;
                      }
                    } catch (error) {
                      logger.error('Failed to fetch cart instance data for simulated payment:', error);
                    }
                  }

                  // Recover the full checkout details persisted on the cart
                  // instance (mirrors the real WiPay branch). The redirect now
                  // carries only a compact `data` blob.
                  const persistedSimulatedSnapshot = resolveCheckoutSnapshotForOrder(
                    cartInstanceData,
                    q.order_id
                  );
                  if (persistedSimulatedSnapshot && typeof persistedSimulatedSnapshot === 'object') {
                    checkoutData = { ...persistedSimulatedSnapshot, ...checkoutData };
                    logger.debug('[WipayResponsePage] Merged persisted checkout snapshot for simulated payment');
                  }
                  
                  // Extract user ID and supermarket ID from the cart structure
                  let actualUserId = checkoutData.userId || cartInstanceData?.userId;
                  let actualSupermarketId =
                    checkoutData.supermarketId ||
                    checkoutData.supermarket_id ||
                    cartInstanceData?.supermarketId;
                  
                  if (resolvedSimulatedCartMatch) {
                    if (!actualSupermarketId) {
                      actualSupermarketId = resolvedSimulatedCartMatch.supermarketId;
                    }
                    if (!actualUserId) {
                      actualUserId = resolvedSimulatedCartMatch.cartDoc.id;
                    }
                  }
                  
                                     // Check existing simulated transaction status before updating
                  let existingSimulatedTransaction = null;
                  try {
                    const existingSimulatedTransactionDoc = await getDoc(transactionRef);
                    if (existingSimulatedTransactionDoc.exists()) {
                      existingSimulatedTransaction = existingSimulatedTransactionDoc.data();
                      logger.debug('[WipayResponsePage] Found existing simulated transaction with status:', existingSimulatedTransaction.status);
                    }
                  } catch (error) {
                    logger.warn('[WipayResponsePage] Error checking existing simulated transaction:', error);
                  }

                  // Determine the appropriate status for simulated transaction
                  let simulatedTransactionStatus = CART_STATUS.AWAITING_PACKING;
                  if (existingSimulatedTransaction) {
                    // If transaction already exists with a more advanced status, preserve it
                    if (StatusHelpers.isMoreAdvancedThanAwaitingPacking(existingSimulatedTransaction.status)) {
                      simulatedTransactionStatus = existingSimulatedTransaction.status;
                      logger.debug('[WipayResponsePage] Preserving existing advanced simulated status:', simulatedTransactionStatus);
                    } else if (existingSimulatedTransaction.status === CART_STATUS.AWAITING_PACKING) {
                      simulatedTransactionStatus = CART_STATUS.AWAITING_PACKING;
                      logger.debug('[WipayResponsePage] Simulated transaction already in awaiting_packing status');
                    } else {
                      // Transaction exists but can be updated to awaiting_packing
                      simulatedTransactionStatus = CART_STATUS.AWAITING_PACKING;
                      logger.debug('[WipayResponsePage] Updating simulated transaction status from', existingSimulatedTransaction.status, 'to awaiting_packing');
                    }
                  }
                  
                                     // Fetch customer name from Users collection for simulated payment (using identity_id)
                  let simulatedCustomerName = checkoutData.customerName || cartInstanceData?.customerName || 'Customer';
                  
                  // Try to get the actual customer name from Users collection by querying identity_id
                  if (actualUserId && actualUserId !== 'simulated_user') {
                    try {
                      logger.debug('[WipayResponsePage] Fetching customer name for simulated payment identity_id:', actualUserId);
                      const usersQuery = query(
                        collection(db, 'Users'),
                        where('identity_id', '==', actualUserId),
                        limit(1)
                      );
                      const userSnapshot = await getDocs(usersQuery);
                      if (!userSnapshot.empty) {
                        const userData = userSnapshot.docs[0].data();
                        simulatedCustomerName = userData.first_name || simulatedCustomerName;
                        logger.debug('[WipayResponsePage] Fetched customer name for simulated payment:', simulatedCustomerName);
                      } else {
                        logger.warn('[WipayResponsePage] User not found for simulated payment identity_id:', actualUserId);
                      }
                    } catch (error) {
                      logger.error('[WipayResponsePage] Error fetching customer name for simulated payment:', error);
                      // Continue with fallback name
                    }
                  }
                  
                  const simulatedGctSummary = resolvePersistedGctSummary(checkoutData, cartInstanceData);

                  // Create simulated transaction using actual checkout data
                  const simulatedTransactionData = {
                    userId: actualUserId || 'simulated_user',
                    cartDocId:
                      resolvedSimulatedCartMatch?.cartDoc?.id ||
                      checkoutData.cartDocId ||
                      q.user_id ||
                      actualUserId ||
                      null,
                    supermarketId: actualSupermarketId || 'simulated_supermarket',
                    supermarketName: checkoutData.supermarketName || cartInstanceData?.supermarketName || 'Simulated Supermarket',
                    customerName: simulatedCustomerName,
                    cartInstanceId:
                      checkoutData.cartInstanceId ||
                      resolvedSimulatedCartMatch?.cartInstanceId ||
                      q.order_id,
                    cartItems: checkoutData.cartItems || cartInstanceData?.products || [
                      {
                        productId: 'simulated_product_1',
                        name: 'Simulated Product 1',
                        price: 500,
                        quantity: 2
                      },
                      {
                        productId: 'simulated_product_2', 
                        name: 'Simulated Product 2',
                        price: 300,
                        quantity: 1
                      }
                    ],
                    confirmationFee: checkoutData.confirmationFee || confirmationFee,
                    orderSubtotal: checkoutData.orderSubtotal || cartInstanceData?.orderSubtotal || 1300,
                    paymentType: checkoutData.paymentType || q.payment_type || 'confirmation_fee_only',
                    deliveryOption: checkoutData.deliveryOption || cartInstanceData?.deliveryOption || 'pickup',
                    deliveryAddress: checkoutData.deliveryAddress || cartInstanceData?.deliveryAddress || null,
                    deliveryPhone: checkoutData.deliveryPhone || cartInstanceData?.deliveryPhone || null,
                    deliveryFee: Number(checkoutData.deliveryFee) || 0,
                    maxDeliveryDistance: checkoutData.maxDeliveryDistance || null,
                    taxInclusiveTotal: checkoutData.taxInclusiveTotal || checkoutData.orderSubtotal || cartInstanceData?.orderSubtotal || 1300,
                    gctIncludedTotal: Number(checkoutData.gctIncludedTotal ?? simulatedGctSummary?.gctIncludedTotal ?? simulatedGctSummary?.totals?.gctIncluded) || 0,
                    taxSummary: simulatedGctSummary,
                    gctSummary: simulatedGctSummary,
                    status: simulatedTransactionStatus,
                     amountPaid: parseFloat(q.total) || confirmationFee,
                     paymentGatewayTransactionId: `sim_pmt_${Date.now()}_${q.order_id}`,
                     paymentGateway: 'Simulated',
                     paymentMethod: 'Simulated Payment',
                     message: 'Simulated payment processed successfully',
                     total: parseFloat(q.total) || confirmationFee,
                     createdAt: existingSimulatedTransaction?.createdAt || serverTimestamp(),
                     updatedAt: serverTimestamp()
                   };
                 
                // Declare simulatedCartFound at the proper scope
                let simulatedCartFound = false;

                if (
                  !cartInstanceData &&
                  (checkoutData.cartInstanceId || q.order_id) &&
                  !resolvedSimulatedCartMatch
                ) {
                  try {
                    const cartDocsForGuard = await getAccessibleCartDocs({
                      checkoutData,
                      fallbackUserId: actualUserId || q.user_id,
                    });
                    const cartMatchForGuard = findCartInstanceInCartDocs(
                      cartDocsForGuard,
                      q.order_id,
                      checkoutData
                    );
                    if (cartMatchForGuard) {
                      resolvedSimulatedCartMatch = cartMatchForGuard;
                      cartInstanceData = cartMatchForGuard.cartInstance;
                    }
                  } catch (guardLookupError) {
                    logger.warn(
                      '[WipayResponsePage] Could not resolve simulated cart instance for fulfillment guard:',
                      guardLookupError
                    );
                  }
                }

                let skipSimulatedSupersededFulfillment =
                  cartInstanceData &&
                  !shouldFulfillSupersededOrderPayment(cartInstanceData, q.order_id);

                const eligibleForSimulatedFreshFulfillment =
                  cartInstanceData && shouldFinalizeSuccessForCart(cartInstanceData, q.order_id);

                let claimedSimulatedCartFulfillment = false;
                if (
                  !skipSimulatedSupersededFulfillment &&
                  eligibleForSimulatedFreshFulfillment &&
                  resolvedSimulatedCartMatch?.cartDoc?.ref &&
                  resolvedSimulatedCartMatch.supermarketId &&
                  resolvedSimulatedCartMatch.cartInstanceId
                ) {
                  const claimResult = await tryClaimCartCheckoutFulfillment({
                    cartDocRef: resolvedSimulatedCartMatch.cartDoc.ref,
                    supermarketId: resolvedSimulatedCartMatch.supermarketId,
                    cartInstanceId: resolvedSimulatedCartMatch.cartInstanceId,
                    orderId: q.order_id,
                  });
                  claimedSimulatedCartFulfillment = claimResult.claimed;
                  if (
                    !claimedSimulatedCartFulfillment &&
                    shouldSkipFulfillmentAfterFailedClaim(
                      cartInstanceData,
                      q.order_id,
                      claimResult.reason
                    )
                  ) {
                    skipSimulatedSupersededFulfillment = true;
                    logger.warn(
                      '[WipayResponsePage] Simulated cart fulfillment already claimed by another payment callback; skipping persistence for unrelated order:',
                      q.order_id,
                      claimResult.reason
                    );
                  }
                }
                 
                                   try {
                    if (skipSimulatedSupersededFulfillment) {
                      logger.warn(
                        '[WipayResponsePage] Skipping simulated fulfillment after cart claim lost for unrelated order:',
                        q.order_id
                      );
                    } else {
                    // Check if transaction already exists to prevent duplicates
                    const existingSimulatedTransaction = await getDoc(transactionRef);
                    if (existingSimulatedTransaction.exists()) {
                      logger.debug('[WipayResponsePage] Simulated transaction already exists, updating instead of creating new one. Cart:', simulatedTransactionData.cartInstanceId, 'WiPay ID:', q.order_id);
                      await setDoc(transactionRef, simulatedTransactionData, { merge: true });
                    } else {
                      logger.debug('[WipayResponsePage] Creating new simulated transaction record. Cart:', simulatedTransactionData.cartInstanceId, 'WiPay ID:', q.order_id);
                      await setDoc(transactionRef, simulatedTransactionData, { merge: true });
                    }
                    setTransactionData(simulatedTransactionData);
                    
                  // Track if notification was sent (declared at higher scope for fallback)
                  let statusWasUpdated = false;
                  let notificationSent = false;

                  if (
                    claimedSimulatedCartFulfillment &&
                    enableEmployeeNotifications &&
                    simulatedTransactionData.supermarketId &&
                    simulatedTransactionData.supermarketId !== 'unknown'
                  ) {
                    logger.debug(
                      '[WipayResponsePage] Notifying employees after simulated atomic cart fulfillment claim'
                    );
                    const customerName = await resolveCustomerNameForNotification(
                      simulatedTransactionData.userId
                    );
                    notifyEmployeesNewOrder({
                      orderId: simulatedTransactionData.cartInstanceId || q.order_id,
                      transactionId: q.order_id,
                      customerName,
                      supermarketId: simulatedTransactionData.supermarketId,
                      logContext: 'WipayResponsePage',
                    });
                    notificationSent = true;
                  }
                    
                  // Only update cart status if simulated transaction status was set to awaiting_packing
                  if (simulatedTransactionStatus === CART_STATUS.AWAITING_PACKING) {
                    logger.debug('[WipayResponsePage] Simulated transaction status is awaiting_packing, updating cart status...');
                    
                    // Find and update the customer's accessible cart document.
                    logger.debug('[WipayResponsePage] Searching for simulated cart instance with order_id:', q.order_id);
                    const cartDocs = await getAccessibleCartDocs({
                      checkoutData,
                      fallbackUserId: actualUserId || q.user_id,
                    });
                    logger.debug('[WipayResponsePage] Found', cartDocs.length, 'accessible cart documents to search for simulated payment');
                    const cartMatch = findCartInstanceInCartDocs(cartDocs, q.order_id, checkoutData);

                    if (cartMatch) {
                              const {
                                cartDoc,
                                cartData,
                                supermarketCart,
                                cartInstanceId: actualSimulatedCartInstanceId,
                                cartInstance: foundSimulatedCartInstance,
                              } = cartMatch;
                              logger.debug('[WipayResponsePage] Found simulated cart instance, checking current status:', foundSimulatedCartInstance.status);
                              
                              if (!shouldFinalizeSuccessForCart(foundSimulatedCartInstance, q.order_id)) {
                                logger.warn(
                                  '[WipayResponsePage] Skipping simulated cart update for superseded or unrelated order_id:',
                                  q.order_id
                                );
                                simulatedCartFound = true;
                              } else if (StatusHelpers.canUpdateToAwaitingPacking(foundSimulatedCartInstance.status)) {
                                applySupersededOrderFulfillmentToCart(
                                  supermarketCart[actualSimulatedCartInstanceId],
                                  q.order_id
                                );
                                logger.debug('[WipayResponsePage] Updating simulated cart status from', foundSimulatedCartInstance.status, 'to awaiting_packing');
                                supermarketCart[actualSimulatedCartInstanceId].status = CART_STATUS.AWAITING_PACKING;
                            supermarketCart[actualSimulatedCartInstanceId].lastUpdatedAt = new Date();
                            if (
                              supermarketCart[actualSimulatedCartInstanceId].pendingCheckoutSnapshots?.[
                                q.order_id
                              ]
                            ) {
                              delete supermarketCart[actualSimulatedCartInstanceId]
                                .pendingCheckoutSnapshots[q.order_id];
                            }
                            
                            await setDoc(cartDoc.ref, cartData, { merge: true });
                            logger.debug('[WipayResponsePage] Cart status updated to awaiting_packing for simulated order:', actualSimulatedCartInstanceId);
                                statusWasUpdated = true;
                            } else if (foundSimulatedCartInstance.status === CART_STATUS.AWAITING_PACKING) {
                              logger.debug('[WipayResponsePage] Simulated cart already in awaiting_packing status - checking if notification needed');
                              
                              // Check if this is a fresh payment (not a page refresh)
                              // If the cart was just updated (within last 30 seconds), send notification
                              const lastUpdatedAt = foundSimulatedCartInstance.lastUpdatedAt;
                              const now = new Date();
                              
                              // Handle both Firestore Timestamp and JavaScript Date objects
                              let lastUpdatedDate = lastUpdatedAt;
                              if (lastUpdatedAt && typeof lastUpdatedAt.toDate === 'function') {
                                lastUpdatedDate = lastUpdatedAt.toDate();
                              } else if (!(lastUpdatedAt instanceof Date)) {
                                lastUpdatedDate = null;
                              }
                              const timeDiff = lastUpdatedDate ? (now - lastUpdatedDate) / 1000 : 999; // seconds
                              
                              // Also check if this is a fresh transaction (created within last 60 seconds)
                              const transactionCreatedAt = simulatedTransactionData.createdAt;
                              
                              // Handle both Firestore Timestamp and JavaScript Date objects
                              let transactionCreatedDate = transactionCreatedAt;
                              if (transactionCreatedAt && typeof transactionCreatedAt.toDate === 'function') {
                                transactionCreatedDate = transactionCreatedAt.toDate();
                              } else if (!(transactionCreatedAt instanceof Date)) {
                                transactionCreatedDate = null;
                              }
                              const transactionTimeDiff = transactionCreatedDate ? (now - transactionCreatedDate) / 1000 : 999;
                              
                              if (timeDiff < 30 || transactionTimeDiff < 60) {
                                logger.debug('[WipayResponsePage] Fresh payment detected (cart updated', Math.round(timeDiff), 's ago, transaction created', Math.round(transactionTimeDiff), 's ago), sending notification');
                                statusWasUpdated = true; // This will trigger notification
                              } else {
                                logger.debug('[WipayResponsePage] Old payment detected (cart updated', Math.round(timeDiff), 's ago, transaction created', Math.round(transactionTimeDiff), 's ago) - likely a page refresh, no notification needed');
                              }
                            } else {
                                logger.warn('[WipayResponsePage] Simulated cart is in more advanced status:', foundSimulatedCartInstance.status, '- cannot update to awaiting_packing');
                              }
                              
                              // Notify employees ONLY if status was actually updated to awaiting_packing
                              if (statusWasUpdated && enableEmployeeNotifications) {
                                logger.debug('[WipayResponsePage] Notifying employees about new simulated order awaiting packing');
                                const customerName = await resolveCustomerNameForNotification(
                                  simulatedTransactionData.userId
                                );
                                notifyEmployeesNewOrder({
                                  orderId: actualSimulatedCartInstanceId,
                                  transactionId: q.order_id,
                                  customerName,
                                  supermarketId: simulatedTransactionData.supermarketId,
                                  logContext: 'WipayResponsePage',
                                });
                                notificationSent = true;
                              } else {
                                logger.debug('[WipayResponsePage] No employee notification sent - cart status was not updated to awaiting_packing');
                              }
                            
                            simulatedCartFound = true;
                    }
                    } else {
                      logger.debug('[WipayResponsePage] Simulated transaction status is', simulatedTransactionStatus, '- skipping cart status update');
                    }
                    
                    // Send notification if transaction was created successfully, even if cart wasn't found or notification wasn't sent
                    if (simulatedTransactionStatus === CART_STATUS.AWAITING_PACKING && enableEmployeeNotifications && !notificationSent) {
                      logger.debug('[WipayResponsePage] Sending fallback employee notification for simulated payment');
                      const customerName = await resolveCustomerNameForNotification(
                        simulatedTransactionData.userId,
                        simulatedTransactionData.customerName || 'Customer'
                      );
                      notifyEmployeesNewOrder({
                        orderId: simulatedTransactionData.cartInstanceId || q.order_id,
                        transactionId: q.order_id,
                        customerName,
                        supermarketId: simulatedTransactionData.supermarketId,
                        logContext: 'WipayResponsePage',
                      });
                    }
                    
                    if (!simulatedCartFound) {
                      logger.warn('[WipayResponsePage] Simulated cart instance not found for order_id:', q.order_id);
                    }
                    }
                  } catch (error) {
                    logger.error('[WipayResponsePage] Error saving simulated transaction:', error);
                    throw error;
                  }
              } else {
                                // For real WiPay payments, use the existing logic
                // Parse the data parameter to get the original checkout data
                let checkoutData = responseCheckoutData;
                if (q.data && Object.keys(checkoutData).length === 0) {
                  checkoutData = parseWipayDataQueryParam(q.data);
                }
                if (Object.keys(checkoutData).length > 0) {
                  logger.debug('[WipayResponsePage] Checkout data for payment:', checkoutData);
                }
               
                // If we don't have checkout data from WiPay, try to fetch it from the cart instance
                let cartInstanceData = null;
                let resolvedCartMatch = null;
                let resolvedSupermarketIdFromUsers = null;
                const needsCartLookup =
                  !checkoutData.cartItems?.length ||
                  !checkoutData.cartInstanceId ||
                  (!checkoutData.supermarketId && !checkoutData.supermarket_id);
                if (needsCartLookup) {
                  try {
                    // First, try to find the cart instance in an accessible cart document.
                    const cartDocs = await getAccessibleCartDocs({
                      checkoutData,
                      fallbackUserId: q.user_id,
                    });
                    const cartMatch = findCartInstanceInCartDocs(cartDocs, q.order_id, checkoutData);
                    if (cartMatch) {
                      resolvedCartMatch = cartMatch;
                      cartInstanceData = cartMatch.cartInstance;
                    }
                    
                    if (!cartInstanceData) {
                      const legacyMatch = await findLegacyCartInUsersByOrderId(q.order_id, {
                        checkoutData,
                        fallbackUserId: q.user_id,
                      });
                      if (legacyMatch) {
                        cartInstanceData = legacyMatch.cartInstanceData;
                        resolvedSupermarketIdFromUsers = legacyMatch.supermarketId;
                      }
                    }
                    
                    if (!cartInstanceData) {
                      // logger.debug('Cart instance data not found in any collection');
                    }
                  } catch (error) {
                    logger.error('Failed to fetch cart instance data:', error);
                  }
                }

                // Recover the full checkout details persisted on the cart
                // instance before redirect. WiPay only echoes a compact `data`
                // blob, so cart items and the GCT breakdown live in this
                // snapshot. Values present on the live `data` param take
                // precedence over the snapshot.
                const persistedCheckoutSnapshot = resolveCheckoutSnapshotForOrder(
                  cartInstanceData,
                  q.order_id
                );
                if (persistedCheckoutSnapshot && typeof persistedCheckoutSnapshot === 'object') {
                  checkoutData = { ...persistedCheckoutSnapshot, ...checkoutData };
                  logger.debug('[WipayResponsePage] Merged persisted checkout snapshot from cart instance');
                }

                if (!cartInstanceData && (checkoutData.cartInstanceId || q.order_id)) {
                  try {
                    const cartDocsForGuard = await getAccessibleCartDocs({
                      checkoutData,
                      fallbackUserId: q.user_id || checkoutData.userId,
                    });
                    const cartMatchForGuard = findCartInstanceInCartDocs(
                      cartDocsForGuard,
                      q.order_id,
                      checkoutData
                    );
                    if (cartMatchForGuard) {
                      resolvedCartMatch = cartMatchForGuard;
                      cartInstanceData = cartMatchForGuard.cartInstance;
                    }
                  } catch (guardLookupError) {
                    logger.warn(
                      '[WipayResponsePage] Could not resolve cart instance for fulfillment guard:',
                      guardLookupError
                    );
                  }
                }

                let skipSupersededFulfillment =
                  cartInstanceData &&
                  !shouldFulfillSupersededOrderPayment(cartInstanceData, q.order_id);

                const eligibleForFreshFulfillment =
                  cartInstanceData && shouldFinalizeSuccessForCart(cartInstanceData, q.order_id);

                let claimedCartFulfillment = false;
                let cartClaimReason = null;
                if (
                  !skipSupersededFulfillment &&
                  eligibleForFreshFulfillment &&
                  resolvedCartMatch?.cartDoc?.ref &&
                  resolvedCartMatch.supermarketId &&
                  resolvedCartMatch.cartInstanceId
                ) {
                  const claimResult = await tryClaimCartCheckoutFulfillment({
                    cartDocRef: resolvedCartMatch.cartDoc.ref,
                    supermarketId: resolvedCartMatch.supermarketId,
                    cartInstanceId: resolvedCartMatch.cartInstanceId,
                    orderId: q.order_id,
                  });
                  claimedCartFulfillment = claimResult.claimed;
                  cartClaimReason = claimResult.reason;
                  if (
                    !claimedCartFulfillment &&
                    shouldSkipFulfillmentAfterFailedClaim(
                      cartInstanceData,
                      q.order_id,
                      claimResult.reason
                    )
                  ) {
                    skipSupersededFulfillment = true;
                    logger.warn(
                      '[WipayResponsePage] Cart fulfillment already claimed by another payment callback; skipping persistence for unrelated order:',
                      q.order_id,
                      claimResult.reason
                    );
                  }
                }

                if (skipSupersededFulfillment) {
                  logger.warn(
                    '[WipayResponsePage] Skipping fulfillment after cart claim lost for unrelated order:',
                    q.order_id,
                    'current orderid:',
                    cartInstanceData.orderid
                  );
                }
               
                // Check existing transaction status before updating
                let existingTransaction = null;
                try {
                  const existingTransactionDoc = await getDoc(transactionRef);
                  if (existingTransactionDoc.exists()) {
                    existingTransaction = existingTransactionDoc.data();
                    logger.debug('[WipayResponsePage] Found existing transaction with status:', existingTransaction.status);
                  }
                } catch (error) {
                  logger.warn('[WipayResponsePage] Error checking existing transaction:', error);
                }

                // Determine the appropriate status based on existing transaction
                let transactionStatus = CART_STATUS.AWAITING_PACKING;
                if (existingTransaction) {
                  // If transaction already exists with a more advanced status, preserve it
                  if (StatusHelpers.isMoreAdvancedThanAwaitingPacking(existingTransaction.status)) {
                    transactionStatus = existingTransaction.status;
                    logger.debug('[WipayResponsePage] Preserving existing advanced status:', transactionStatus);
                  } else if (existingTransaction.status === CART_STATUS.AWAITING_PACKING) {
                    transactionStatus = CART_STATUS.AWAITING_PACKING;
                    logger.debug('[WipayResponsePage] Transaction already in awaiting_packing status');
                  } else {
                    // Transaction exists but can be updated to awaiting_packing
                    transactionStatus = CART_STATUS.AWAITING_PACKING;
                    logger.debug('[WipayResponsePage] Updating transaction status from', existingTransaction.status, 'to awaiting_packing');
                  }
                }
                // Only skip when inventory was actually decremented (or order advanced past packing).
                // Do not use AWAITING_PACKING alone: the transaction is saved with that status before
                // decrement runs, so a refresh after a failed decrement would otherwise skip forever.
                const inventoryAlreadyProcessed =
                  existingTransaction?.inventoryDecremented === true ||
                  StatusHelpers.isMoreAdvancedThanAwaitingPacking(existingTransaction?.status);
               
                // Fetch customer name from Users collection using userId (which is identity_id)
                const userId = checkoutData.userId || cartInstanceData?.userId || q.user_id || 'unknown';
                let customerName = checkoutData.customerName || cartInstanceData?.customerName || q.customer_name || 'Customer';
                
                // Try to get the actual customer name from Users collection by querying identity_id
                if (userId && userId !== 'unknown') {
                  try {
                    logger.debug('[WipayResponsePage] Fetching customer name for identity_id:', userId);
                    const usersQuery = query(
                      collection(db, 'Users'),
                      where('identity_id', '==', userId),
                      limit(1)
                    );
                    const userSnapshot = await getDocs(usersQuery);
                    if (!userSnapshot.empty) {
                      const userData = userSnapshot.docs[0].data();
                      customerName = userData.first_name || customerName;
                      logger.debug('[WipayResponsePage] Fetched customer name:', customerName);
                    } else {
                      logger.warn('[WipayResponsePage] User not found for identity_id:', userId);
                    }
                  } catch (error) {
                    logger.error('[WipayResponsePage] Error fetching customer name:', error);
                    // Continue with fallback name
                  }
                }
                
                const resolvedSupermarketId =
                  checkoutData.supermarketId ||
                  checkoutData.supermarket_id ||
                  cartInstanceData?.supermarketId ||
                  resolvedCartMatch?.supermarketId ||
                  resolvedSupermarketIdFromUsers ||
                  q.supermarket_id ||
                  'unknown';

                const resolvedCartInstanceId =
                  checkoutData.cartInstanceId ||
                  resolvedCartMatch?.cartInstanceId ||
                  q.order_id;

                const persistedGctSummary = resolvePersistedGctSummary(checkoutData, cartInstanceData, q);

                const transactionData = {
                  userId: userId,
                  cartDocId:
                    resolvedCartMatch?.cartDoc?.id ||
                    checkoutData.cartDocId ||
                    q.user_id ||
                    null,
                  supermarketId: resolvedSupermarketId,
                  supermarketName: checkoutData.supermarketName || cartInstanceData?.supermarketName || q.supermarket_name || 'Unknown Supermarket',
                  customerName: customerName,
                  cartInstanceId: resolvedCartInstanceId,
                  cartItems: checkoutData.cartItems || cartInstanceData?.products || (q.cart_items ? JSON.parse(q.cart_items) : []),
                  confirmationFee: checkoutData.confirmationFee || confirmationFee,
                  orderSubtotal: checkoutData.orderSubtotal || cartInstanceData?.orderSubtotal || parseFloat(q.order_subtotal) || 0,
                  paymentType: checkoutData.paymentType || q.payment_type || 'confirmation_fee_only',
                  deliveryOption: checkoutData.deliveryOption || cartInstanceData?.deliveryOption || q.delivery_option || 'pickup',
                  deliveryAddress: checkoutData.deliveryAddress || cartInstanceData?.deliveryAddress || q.delivery_address || null,
                  deliveryPhone: checkoutData.deliveryPhone || cartInstanceData?.deliveryPhone || q.delivery_phone || null,
                  deliveryFee:
                    Number(
                      checkoutData.deliveryFee ??
                        checkoutData.delivery_fee ??
                        cartInstanceData?.deliveryFee
                    ) || 0,
                  maxDeliveryDistance: checkoutData.maxDeliveryDistance || null,
                  taxInclusiveTotal: checkoutData.taxInclusiveTotal || checkoutData.orderSubtotal || cartInstanceData?.orderSubtotal || parseFloat(q.order_subtotal) || 0,
                  gctIncludedTotal: Number(checkoutData.gctIncludedTotal ?? q.gct_included_total ?? persistedGctSummary?.gctIncludedTotal ?? persistedGctSummary?.totals?.gctIncluded) || 0,
                  taxSummary: persistedGctSummary,
                  gctSummary: persistedGctSummary,
                  status: transactionStatus,
                  amountPaid: parseFloat(q.total) || (checkoutData.paymentType === 'full'
                    ? ((checkoutData.orderSubtotal || 0) + (checkoutData.confirmationFee || confirmationFee) + (Number(checkoutData.deliveryFee) || 0))
                    : ((checkoutData.confirmationFee || confirmationFee) + (Number(checkoutData.deliveryFee) || 0))),
                  paymentGatewayTransactionId: q.transaction_id || `wipay_${Date.now()}`,
                  paymentGateway: paymentGateway,
                  paymentMethod: q.method || paymentMethod,
                  message: q.message || 'Payment processed successfully',
                  total: parseFloat(q.total) || (checkoutData.paymentType === 'full'
                    ? ((checkoutData.orderSubtotal || 0) + (checkoutData.confirmationFee || confirmationFee) + (Number(checkoutData.deliveryFee) || 0))
                    : ((checkoutData.confirmationFee || confirmationFee) + (Number(checkoutData.deliveryFee) || 0))),
                  createdAt: existingTransaction?.createdAt || serverTimestamp(),
                  updatedAt: serverTimestamp(),
                  ...(existingTransaction?.inventoryDecremented === true
                    ? { inventoryDecremented: true }
                    : {}),
                };
                
                // Declare cartFound at the proper scope
                let cartFound = false;
                
                try {
                  if (skipSupersededFulfillment) {
                    logger.debug(
                      '[WipayResponsePage] Superseded callback ignored; cart instance already fulfilled'
                    );
                  } else {
                  // Check if transaction already exists to prevent duplicates
                  const existingTransactionSnap = await getDoc(transactionRef);
                  if (existingTransactionSnap.exists()) {
                    logger.debug('[WipayResponsePage] Real WiPay transaction already exists, updating instead of creating new one. Cart:', transactionData.cartInstanceId, 'WiPay ID:', q.order_id);
                    await setDoc(transactionRef, transactionData, { merge: true });
                  } else {
                    logger.debug('[WipayResponsePage] Creating new real WiPay transaction record. Cart:', transactionData.cartInstanceId, 'WiPay ID:', q.order_id);
                    await setDoc(transactionRef, transactionData, { merge: true });
                  }
                  setTransactionData(transactionData);
                  
                  // Decrement inventory exactly once for successful real WiPay payments.
                  const inventorySupermarketId = transactionData.supermarketId;
                  const inventoryCartItems = Array.isArray(transactionData.cartItems)
                    ? transactionData.cartItems
                    : [];
                  const shouldRunInventoryDecrement = shouldRunInventoryDecrementForOrder({
                    cartInstanceData,
                    orderId: q.order_id,
                    eligibleForFreshFulfillment,
                    claimedCartFulfillment,
                    hasResolvedCartDoc: Boolean(resolvedCartMatch?.cartDoc?.ref),
                    claimReason: cartClaimReason,
                  });
                  const skipInventoryForSupersededCart = !shouldRunInventoryDecrement;

                  if (inventoryAlreadyProcessed) {
                    logger.debug('[WipayResponsePage] Skipping inventory decrement; transaction was already processed');
                  } else if (skipInventoryForSupersededCart) {
                    logger.warn(
                      '[WipayResponsePage] Skipping inventory decrement for unrelated order or stale cart snapshot:',
                      q.order_id
                    );
                  } else if (
                    inventorySupermarketId &&
                    inventorySupermarketId !== 'unknown' &&
                    inventoryCartItems.length > 0
                  ) {
                    const claimedDecrement = await tryClaimInventoryDecrement(transactionRef);
                    if (claimedDecrement) {
                      logger.debug('[WipayResponsePage] Decrementing inventory for successful real WiPay payment');
                      const decrementProgress = existingTransaction?.inventoryDecrementProgress || [];
                      const itemsForDecrement = cartItemsRemainingForDecrement(
                        inventoryCartItems,
                        decrementProgress
                      );
                      const { allSucceeded: inventoryDecrementSucceeded, decrementedItems } =
                        await decrementInventory(inventorySupermarketId, itemsForDecrement);
                      if (inventoryDecrementSucceeded) {
                        if (decrementProgress.length > 0) {
                          await updateDoc(transactionRef, buildDecrementSuccessUpdate());
                        }
                      } else if (decrementedItems.length > 0) {
                        logger.warn(
                          '[WipayResponsePage] Inventory decrement incomplete; persisting progress for retry'
                        );
                        await updateDoc(
                          transactionRef,
                          buildPartialDecrementFailureUpdate(decrementProgress, decrementedItems)
                        );
                      } else {
                        logger.warn(
                          '[WipayResponsePage] Inventory decrement incomplete; clearing flag so a refresh can retry'
                        );
                        await updateDoc(transactionRef, {
                          inventoryDecremented: false,
                          updatedAt: serverTimestamp(),
                        });
                      }
                    } else {
                      logger.debug(
                        '[WipayResponsePage] Inventory decrement already claimed by another request'
                      );
                    }
                  } else {
                    logger.warn(
                      '[WipayResponsePage] Cannot decrement inventory: missing supermarketId or cartItems',
                      { inventorySupermarketId, cartItemCount: inventoryCartItems.length }
                    );
                  }

                  if (
                    claimedCartFulfillment &&
                    enableEmployeeNotifications &&
                    transactionData.supermarketId &&
                    transactionData.supermarketId !== 'unknown'
                  ) {
                    logger.debug(
                      '[WipayResponsePage] Notifying employees after atomic cart fulfillment claim'
                    );
                    const customerName = await resolveCustomerNameForNotification(
                      transactionData.userId
                    );
                    notifyEmployeesNewOrder({
                      orderId: resolvedCartInstanceId,
                      transactionId: q.order_id,
                      customerName,
                      supermarketId: transactionData.supermarketId,
                      logContext: 'WipayResponsePage',
                    });
                  }
                  
                  // Only update cart status if transaction status was set to awaiting_packing
                  if (transactionStatus === CART_STATUS.AWAITING_PACKING) {
                    logger.debug('[WipayResponsePage] Transaction status is awaiting_packing, updating cart status...');
                  
                  // Find and update the customer's accessible cart document.
                  logger.debug('[WipayResponsePage] Searching for cart instance with order_id:', q.order_id);
                  const cartDocs = await getAccessibleCartDocs({
                    checkoutData,
                    fallbackUserId: transactionData.userId || q.user_id,
                  });
                  logger.debug('[WipayResponsePage] Found', cartDocs.length, 'accessible cart documents to search');
                    const cartMatch = findCartInstanceInCartDocs(cartDocs, q.order_id, checkoutData);
                  if (cartMatch) {
                            const {
                              cartDoc,
                              cartData,
                              supermarketCart,
                              cartInstanceId: actualCartInstanceId,
                              cartInstance: foundCartInstance,
                            } = cartMatch;
                            logger.debug('[WipayResponsePage] Found cart instance, checking current status:', foundCartInstance.status);

                            if (!shouldFinalizeSuccessForCart(foundCartInstance, q.order_id)) {
                              logger.warn(
                                '[WipayResponsePage] Skipping cart update for superseded or unrelated order_id:',
                                q.order_id
                              );
                              cartFound = true;
                            } else {
                            
                            // Only update status if it's safe to do so (not more advanced than awaiting_packing)
                            let statusWasUpdated = false;
                            if (StatusHelpers.canUpdateToAwaitingPacking(foundCartInstance.status)) {
                              applySupersededOrderFulfillmentToCart(
                                supermarketCart[actualCartInstanceId],
                                q.order_id
                              );
                              logger.debug('[WipayResponsePage] Updating status from', foundCartInstance.status, 'to awaiting_packing');
                              supermarketCart[actualCartInstanceId].status = CART_STATUS.AWAITING_PACKING;
                          supermarketCart[actualCartInstanceId].lastUpdatedAt = new Date();
                          if (
                            supermarketCart[actualCartInstanceId].pendingCheckoutSnapshots?.[q.order_id]
                          ) {
                            delete supermarketCart[actualCartInstanceId].pendingCheckoutSnapshots[
                              q.order_id
                            ];
                          }
                          
                          await setDoc(cartDoc.ref, cartData, { merge: true });
                          logger.debug('[WipayResponsePage] Cart status updated to awaiting_packing for order:', actualCartInstanceId);
                              statusWasUpdated = true;
                            } else if (foundCartInstance.status === CART_STATUS.AWAITING_PACKING) {
                              logger.debug('[WipayResponsePage] Cart already in awaiting_packing status - checking if notification needed');
                              
                              // Check if this is a fresh payment (not a page refresh)
                              // If the cart was just updated (within last 30 seconds), send notification
                              const lastUpdatedAt = foundCartInstance.lastUpdatedAt;
                              const now = new Date();
                              
                              // Handle both Firestore Timestamp and JavaScript Date objects
                              let lastUpdatedDate = lastUpdatedAt;
                              if (lastUpdatedAt && typeof lastUpdatedAt.toDate === 'function') {
                                lastUpdatedDate = lastUpdatedAt.toDate();
                              } else if (!(lastUpdatedAt instanceof Date)) {
                                lastUpdatedDate = null;
                              }
                              const timeDiff = lastUpdatedDate ? (now - lastUpdatedDate) / 1000 : 999; // seconds
                              
                              // Also check if this is a fresh transaction (created within last 60 seconds)
                              const transactionCreatedAt = transactionData.createdAt;
                              
                              // Handle both Firestore Timestamp and JavaScript Date objects
                              let transactionCreatedDate = transactionCreatedAt;
                              if (transactionCreatedAt && typeof transactionCreatedAt.toDate === 'function') {
                                transactionCreatedDate = transactionCreatedAt.toDate();
                              } else if (!(transactionCreatedAt instanceof Date)) {
                                transactionCreatedDate = null;
                              }
                              const transactionTimeDiff = transactionCreatedDate ? (now - transactionCreatedDate) / 1000 : 999;
                              
                              if (timeDiff < 30 || transactionTimeDiff < 60) {
                                logger.debug('[WipayResponsePage] Fresh payment detected (cart updated', Math.round(timeDiff), 's ago, transaction created', Math.round(transactionTimeDiff), 's ago), sending notification');
                                statusWasUpdated = true; // This will trigger notification
                              } else {
                                logger.debug('[WipayResponsePage] Old payment detected (cart updated', Math.round(timeDiff), 's ago, transaction created', Math.round(transactionTimeDiff), 's ago) - likely a page refresh, no notification needed');
                              }
                            } else {
                              logger.warn('[WipayResponsePage] Cart is in more advanced status:', foundCartInstance.status, '- cannot update to awaiting_packing');
                            }
                            
                            // Notify employees ONLY if status was actually updated to awaiting_packing
                            logger.debug('[WipayResponsePage] Notification check:', {
                              statusWasUpdated,
                              enableEmployeeNotifications,
                              hasSupermarketId: !!transactionData.supermarketId,
                              supermarketId: transactionData.supermarketId,
                              orderId: actualCartInstanceId,
                              transactionId: docId
                            });
                            
                            if (statusWasUpdated && enableEmployeeNotifications) {
                              logger.debug('[WipayResponsePage] Notifying employees about new order awaiting packing');
                              const customerName = await resolveCustomerNameForNotification(transactionData.userId);
                              notifyEmployeesNewOrder({
                                orderId: actualCartInstanceId,
                                transactionId: docId,
                                customerName,
                                supermarketId: transactionData.supermarketId,
                                logContext: 'WipayResponsePage',
                              });
                            } else {
                              logger.debug('[WipayResponsePage] No employee notification sent - cart status was not updated to awaiting_packing');
                              logger.info('[WipayResponsePage] ⏭️ No employee notification sent - cart status was not updated to awaiting_packing');
                            }
                          
                          cartFound = true;
                            }
                  }
                  } else {
                    logger.debug('[WipayResponsePage] Transaction status is', transactionStatus, '- skipping cart status update');
                  }
                  
                  if (!cartFound) {
                    logger.warn('[WipayResponsePage] Cart instance not found for order_id:', q.order_id);
                  }
                  }
                } catch (error) {
                  logger.error('[WipayResponsePage] Error saving real WiPay transaction:', error);
                  throw error;
                }
              }
            } else if (q.status !== 'success') {
              // Payment failed - show failure message, don't restore cart
              logger.debug('[WipayResponsePage] Payment failed - no cart restoration needed');
              logger.debug('[WipayResponsePage] Order ID:', q.order_id, 'Status:', q.status);
              logger.debug('[WipayResponsePage] Full payment params:', q);
              
              // For failed payments, extract transaction data for navigation purposes
              let failedTransactionData = null;
              
              // Try to parse checkout data from the data parameter for failed payments
              if (q.data) {
                try {
                  const checkoutData = parseWipayDataQueryParam(q.data);
                  logger.debug('[WipayResponsePage] Parsed checkout data for failed payment:', checkoutData);
                  
                  failedTransactionData = {
                    supermarketId: checkoutData.supermarketId || q.supermarket_id || 'unknown',
                    cartInstanceId: checkoutData.cartInstanceId || q.order_id,
                    userId: checkoutData.userId || q.user_id || 'unknown',
                    supermarketName: checkoutData.supermarketName || q.supermarket_name || 'Unknown Supermarket',
                    customerName: checkoutData.customerName || q.customer_name || 'Unknown Customer',
                    cartItems: checkoutData.cartItems || [],
                    confirmationFee: checkoutData.confirmationFee || confirmationFee,
                    orderSubtotal: checkoutData.orderSubtotal || parseFloat(q.order_subtotal) || 0,
                    paymentType: checkoutData.paymentType || q.payment_type || 'confirmation_fee_only',
                    deliveryOption: checkoutData.deliveryOption || q.delivery_option || 'pickup',
                    deliveryAddress: checkoutData.deliveryAddress || q.delivery_address || null,
                    deliveryPhone: checkoutData.deliveryPhone || q.delivery_phone || null,
                    deliveryFee: Number(checkoutData.deliveryFee) || 0,
                    maxDeliveryDistance: checkoutData.maxDeliveryDistance || null,
                    status: CART_STATUS.PAYMENT_FAILED,
                    amountPaid: 0,
                    paymentGatewayTransactionId: q.transaction_id || `failed_${Date.now()}`,
                    paymentGateway: paymentGateway,
                    paymentMethod: q.method || paymentMethod,
                    message: q.message || 'Payment failed',
                    total: parseFloat(q.total) || confirmationFee,
                    createdAt: new Date(),
                    updatedAt: new Date()
                  };
                } catch (parseError) {
                  logger.error('[WipayResponsePage] Failed to parse checkout data for failed payment:', parseError);
                  // Create minimal transaction data with available information
                  failedTransactionData = {
                    supermarketId: q.supermarket_id || 'unknown',
                    cartInstanceId: q.order_id || 'unknown',
                    userId: q.user_id || 'unknown',
                    supermarketName: q.supermarket_name || 'Unknown Supermarket',
                    customerName: q.customer_name || 'Unknown Customer',
                    cartItems: q.cart_items ? JSON.parse(q.cart_items) : [],
                    confirmationFee: parseFloat(q.total) || confirmationFee,
                    orderSubtotal: parseFloat(q.order_subtotal) || 0,
                    deliveryOption: q.delivery_option || 'pickup',
                    deliveryAddress: q.delivery_address || null,
                    deliveryPhone: q.delivery_phone || null,
                    status: CART_STATUS.PAYMENT_FAILED,
                    amountPaid: 0,
                    paymentGatewayTransactionId: q.transaction_id || `failed_${Date.now()}`,
                    paymentGateway: paymentGateway,
                    paymentMethod: q.method || paymentMethod,
                    message: q.message || 'Payment failed',
                    total: parseFloat(q.total) || confirmationFee,
                    createdAt: new Date(),
                    updatedAt: new Date()
                  };
                }
              }
              
              // Set transaction data for failed payments to enable proper navigation
              if (failedTransactionData) {
                setTransactionData(failedTransactionData);
                logger.debug('[WipayResponsePage] Set transaction data for failed payment:', failedTransactionData);
              }
              
              // For failed payments, just log the failure and show the error page
              // Note: No inventory restoration needed as simulated payments only decrement on success
              logger.debug('[WipayResponsePage] Payment failed - no cart restoration needed for failed payments');
              setCartRestored(false);
            }

            setSaving(false);
        };

    const savePayment = async () => {
      try {
        await promiseWithTimeout(
          processPaymentCallback(),
          WIPAY_CALLBACK_PROCESSING_TIMEOUT_MS,
          'WiPay callback processing timed out'
        );
      } catch (err) {
        const timedOut =
          err?.message?.includes('timed out') || err?.message?.includes('WiPay callback');
        if (timedOut) {
          logger.error('[WipayResponsePage] Callback processing timed out:', err);
          setCallbackTimedOut(true);
          return;
        }
        setError(`Failed to save payment details: ${err.message}`);
      } finally {
        setBackgroundProcessing(false);
      }
    };
    savePayment();
    // eslint-disable-next-line
  }, []);

  const isSuccess = params.status === 'success';
  
  // Helper function to determine if this is a full payment order
  // Checks paymentType field first, then falls back to comparing amounts
  const isFullPayment = useCallback(() => {
    if (!transactionData) return false;
    
    // Check paymentType field first
    if (transactionData.paymentType === 'full' || params.payment_type === 'full') {
      return true;
    }
    
    // Fallback: Check if amount paid equals order subtotal + confirmation fee
    const orderSubtotal = transactionData.orderSubtotal || 0;
    const confirmationFeeAmount = transactionData.confirmationFee || confirmationFee;
    const deliveryFeeAmount = Number(transactionData.deliveryFee) || 0;
    const expectedFullTotal = orderSubtotal + confirmationFeeAmount + deliveryFeeAmount;
    const amountPaid = parseFloat(transactionData.amountPaid) || parseFloat(transactionData.total) || parseFloat(params.total) || 0;
    
    // If amount paid is close to or equals the full total (within 0.01 for rounding), it's a full payment
    if (orderSubtotal > 0 && Math.abs(amountPaid - expectedFullTotal) < 0.01) {
      return true;
    }
    
    return false;
  }, [transactionData, params.payment_type, params.total, confirmationFee]);
  
  // Debug logging for payment status
  useEffect(() => {
    if (params.status) {
      logger.debug('[WipayResponsePage] Payment status detected:', params.status);
      logger.debug('[WipayResponsePage] isSuccess calculated as:', isSuccess);
      logger.debug('[WipayResponsePage] cartRestored state:', cartRestored);
      if (transactionData) {
        logger.debug('[WipayResponsePage] transactionData.paymentType:', transactionData.paymentType);
        logger.debug('[WipayResponsePage] params.payment_type:', params.payment_type);
        logger.debug('[WipayResponsePage] isFullPayment():', isFullPayment());
        logger.debug('[WipayResponsePage] amountPaid:', transactionData.amountPaid || transactionData.total || params.total);
        logger.debug('[WipayResponsePage] orderSubtotal:', transactionData.orderSubtotal);
        logger.debug('[WipayResponsePage] confirmationFee:', transactionData.confirmationFee);
      }
    }
  }, [params.status, params.payment_type, params.total, isSuccess, cartRestored, transactionData, isFullPayment]);

  const displayedTaxSummary = resolvePersistedGctSummary(transactionData, params);

  return (
    <Layout>
      <NoIndexHelmet title="Payment Response | Pack My Cart" path="/payment/response" />
      <style>
        {`
          .payment-response-card {
            border: none;
            border-radius: 12px;
          }
          .payment-response-card .btn {
            border-radius: 8px;
            font-weight: 500;
            transition: all 0.2s ease-in-out;
          }
          .payment-response-card .btn:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.15);
          }
          .payment-response-card .btn-primary {
            background: linear-gradient(135deg, #007bff, #0056b3);
            border: none;
          }
          .payment-response-card .btn-success {
            background: linear-gradient(135deg, #28a745, #1e7e34);
            border: none;
          }
          .payment-response-card .btn-warning {
            background: linear-gradient(135deg, #ffc107, #e0a800);
            border: none;
            color: #212529;
          }
          .payment-response-card .btn-outline-secondary {
            border: 2px solid #6c757d;
            color: #6c757d;
          }
          .payment-response-card .btn-outline-secondary:hover {
            background-color: #6c757d;
            border-color: #6c757d;
            color: white;
          }
        `}
      </style>
      <Container className="mt-3 mt-md-5 px-3">
        <Card className="mx-auto shadow-sm payment-response-card" style={{maxWidth: 600}}>
          <Card.Body>
            <Card.Title className={`text-center mb-4 ${isSuccess ? "text-success" : "text-danger"}`}>
              <i className={`fas ${isSuccess ? 'fa-check-circle' : 'fa-times-circle'} me-2`}></i>
              {isSuccess ? "Payment Successful" : "Payment Failed"}
            </Card.Title>
            {saving && <Spinner animation="border" className="my-3" />}
            {backgroundProcessing && (
              <div className="text-center text-muted small mb-3">
                <Spinner animation="border" size="sm" className="me-2" />
                Finalizing your order in the background…
              </div>
            )}
            {callbackTimedOut && (
              <Alert variant="warning">
                <i className="fas fa-clock me-2"></i>
                <strong>Still processing your payment</strong>
                <br />
                WiPay confirmed your payment, but saving order details is taking longer than
                expected. This receipt shows what WiPay returned. Refresh this page in a minute if
                order status does not update, or contact support with your transaction ID.
              </Alert>
            )}
            {error && (
              <Alert variant={invalidCartStatus ? "warning" : "danger"}>
                {invalidCartStatus ? (
                  <>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <strong>Invalid Cart Status</strong>
                    <br />
                    {error}
                    <br />
                    <small className="text-muted">Order ID: {params.order_id}</small>
                  </>
                ) : (
                  error
                )}
              </Alert>
            )}
            {!saving && (!error || callbackTimedOut) && (
              <div>
                {/* Payment Failed Notice */}
                {!isSuccess && (
                  <Alert variant={cartRestored ? "warning" : "danger"} className="mb-3">
                    <i className={`fas ${cartRestored ? 'fa-exclamation-triangle' : 'fa-times-circle'} me-2`}></i>
                    <strong>Payment Failed</strong>
                    <br />
                    {cartRestored ? (
                      <>
                        Your cart has been restored to active status. You can:
                        <ul className="mb-0 mt-2">
                          <li>Review and modify your order</li>
                          <li>Try the payment again</li>
                          <li>Contact support if the issue persists</li>
                        </ul>
                      </>
                    ) : (
                      <>
                        <small className="text-muted">Order ID: {params.order_id}</small>
                      </>
                    )}
                  </Alert>
                )}
                
                {/* Full Payment Notice for successful payments */}
                {isSuccess && isFullPayment() && (
                  <Alert variant="success" className="mb-3 text-center">
                    <strong><i className="fas fa-check-circle me-2"></i>FULL PAYMENT ORDER</strong>
                    <br />
                    <small>The full order total has been paid. Confirmation fee collected as processing fee.</small>
                  </Alert>
                )}
                
                <div className="row text-start">
                  <div className="col-md-6">
                    <p className="mb-2"><strong>Customer Name:</strong> <span className="text-break">{transactionData?.customerName || 'Customer'}</span></p>
                    <p className="mb-2"><strong>Order ID:</strong> <span className="text-break font-monospace">{transactionData?.cartInstanceId || params.order_id}</span></p>
                    <p className="mb-2"><strong>Supermarket:</strong> <span className="text-break">{transactionData?.supermarketName || 'Unknown Supermarket'}</span></p>
                    <p className="mb-2"><strong>Status:</strong> <span className={`badge ${invalidCartStatus ? 'bg-warning' : isSuccess ? 'bg-success' : 'bg-danger'}`}>{invalidCartStatus ? StatusHelpers.getDisplayText(cartStatus) : (transactionData?.status || params.status)}</span></p>
                    <p className="mb-2"><strong>Payment Type:</strong> {isFullPayment() ? (
                      <span className="badge bg-success"><i className="fas fa-check-circle me-1"></i>Full Payment</span>
                    ) : (
                      <span className="badge bg-secondary">Confirmation Fee Only</span>
                    )}</p>
                    <p className="mb-2"><strong>Message:</strong> <span className="text-break">{transactionData?.message || params.message || 'Payment processed'}</span></p>
                    <p className="mb-2"><strong>Transaction ID:</strong> <span className="text-break font-monospace">{transactionData?.paymentGatewayTransactionId || params.transaction_id || 'N/A'}</span></p>
                    {isFullPayment() ? (
                      <>
                        <p className="mb-2"><strong>Order Subtotal:</strong> <span className="fw-bold">${(Number(transactionData?.orderSubtotal) || 0).toFixed(2)}</span> <span className="text-success">(Paid)</span></p>
                        {(Number(transactionData?.deliveryFee) || 0) > 0 && (
                          <p className="mb-2"><strong>Delivery Fee:</strong> <span className="fw-bold">${(Number(transactionData?.deliveryFee) || 0).toFixed(2)}</span> <span className="text-success">(Paid)</span></p>
                        )}
                        <p className="mb-2"><strong>Processing Fee (Confirmation Fee):</strong> <span className="fw-bold">${(Number(transactionData?.confirmationFee) || confirmationFee).toFixed(2)}</span> <span className="text-muted">(Included)</span></p>
                        <p className="mb-2"><strong>Total Paid:</strong> <span className="fw-bold fs-5 text-success">${((Number(transactionData?.orderSubtotal) || 0) + (Number(transactionData?.confirmationFee) || confirmationFee) + (Number(transactionData?.deliveryFee) || 0)).toFixed(2)} {params.currency || 'JMD'}</span></p>
                        <p className="mb-2"><strong className="text-success">No amount due {(transactionData?.deliveryOption || params.delivery_option) === 'delivery' ? 'on delivery' : 'at pickup'}</strong></p>
                      </>
                    ) : (
                      <>
                        <p className="mb-2"><strong>Confirmation Fee Paid:</strong> <span className="fw-bold">${(parseFloat(transactionData?.amountPaid) || parseFloat(transactionData?.total) || parseFloat(params.total) || 0).toFixed(2)} {params.currency || 'JMD'}</span></p>
                        {(Number(transactionData?.deliveryFee) || 0) > 0 && (
                          <p className="mb-2"><strong>Delivery Fee Paid:</strong> <span className="fw-bold">${(Number(transactionData?.deliveryFee) || 0).toFixed(2)}</span> <span className="text-success">(Included above)</span></p>
                        )}
                        <p className="mb-2"><strong>Order Subtotal:</strong> <span className="fw-bold">${(Number(transactionData?.orderSubtotal) || 0).toFixed(2)}</span> <span className="text-muted">({(transactionData?.deliveryOption || params.delivery_option) === 'delivery' ? 'Due on delivery' : 'Due at pickup'})</span></p>
                        <p className="mb-2"><strong className="text-warning">Amount Due {(transactionData?.deliveryOption || params.delivery_option) === 'delivery' ? 'on Delivery' : 'at Pickup'}: ${(Number(transactionData?.orderSubtotal) || 0).toFixed(2)}</strong></p>
                      </>
                    )}
                    {displayedTaxSummary && (
                      <GctSummaryBreakdown taxSummary={displayedTaxSummary} className="mb-2 p-2 border rounded bg-light" compact />
                    )}
                  </div>
                  <div className="col-md-6">
                    <p className="mb-2"><strong>Payment Gateway:</strong> <span className="text-break">{transactionData?.paymentGateway || paymentGateway}</span></p>
                    <p className="mb-2"><strong>Currency:</strong> {params.currency || 'JMD'}</p>
                    <p className="mb-2"><strong>Method:</strong> <span className="text-break">{transactionData?.paymentMethod || params.method || paymentMethod}</span></p>
                    {(transactionData?.deliveryOption || params.delivery_option) && (
                      <p className="mb-2"><strong>Delivery Option:</strong> <span className={`badge ${(transactionData?.deliveryOption || params.delivery_option) === 'delivery' ? 'bg-info' : 'bg-secondary'}`}>{transactionData?.deliveryOption || params.delivery_option}</span></p>
                    )}
                    {(transactionData?.deliveryAddress || params.delivery_address) && (
                      <p className="mb-2"><strong>Delivery Address:</strong> <span className="text-break">{transactionData?.deliveryAddress || params.delivery_address}</span></p>
                    )}
                    {(transactionData?.deliveryPhone || params.delivery_phone) && (
                      <p className="mb-2"><strong>Delivery Phone:</strong> <span className="text-break">{transactionData?.deliveryPhone || params.delivery_phone}</span></p>
                    )}
                    {(Number(transactionData?.deliveryFee) || 0) > 0 && (
                      <p className="mb-2"><strong>Delivery Fee:</strong> <span className="fw-bold">${(Number(transactionData?.deliveryFee) || 0).toFixed(2)}</span></p>
                    )}
                    {transactionData?.maxDeliveryDistance && (
                      <p className="mb-2"><strong>Max Delivery Distance:</strong> {transactionData.maxDeliveryDistance} km</p>
                    )}
                  </div>
                </div>
                
                {/* FCM Notification Permission for successful payments - skip on iOS Safari (limited support, can cause blank screen) */}
                {isSuccess && !isIOSOrMobileSafari() && (
                  <div className="notification-permission-wrapper" style={{ marginBottom: '1rem' }}>
                    <NotificationPermission />
                  </div>
                )}
                
                <div className="mt-4">
                  <div className="row g-2 g-md-3 justify-content-center">
                    <div className="col-12 col-md-4">
                      <Button 
                        variant="primary" 
                        onClick={() => navigate('/')} 
                        className="w-100 py-2 py-md-3"
                        size="lg"
                        style={{
                          fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                          fontWeight: '500'
                        }}
                      >
                        <i className="fas fa-home me-2"></i>
                        <span className="d-none d-sm-inline">Return Home</span>
                        <span className="d-sm-none">Home</span>
                      </Button>
                    </div>
                    
                    {cartTransactionId && (
                      <div className="col-12 col-md-4">
                        <Button
                          variant="success"
                          onClick={() => navigate(`/order-status/${cartTransactionId}`)}
                          className="w-100 py-2 py-md-3"
                          size="lg"
                          style={{
                            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                            fontWeight: '500'
                          }}
                        >
                          <i className="fas fa-eye me-2"></i>
                          <span className="d-none d-sm-inline">View Order</span>
                          <span className="d-sm-none">View</span>
                        </Button>
                      </div>
                    )}
                    
                    <div className="col-12 col-md-4">
                      <Button 
                        variant="outline-secondary" 
                        onClick={() => navigate('/carts')}
                        className="w-100 py-2 py-md-3"
                        size="lg"
                        style={{
                          fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                          fontWeight: '500'
                        }}
                      >
                        <i className="fas fa-shopping-cart me-2"></i>
                        <span className="d-none d-sm-inline">Back to Carts</span>
                        <span className="d-sm-none">Carts</span>
                      </Button>
                    </div>

                    {/* Print Receipt Button */}
                    {transactionData && (
                      <div className="col-12 col-md-4">
                        <Button 
                          variant="info" 
                          onClick={generateReceipt}
                          className="w-100 py-2 py-md-3"
                          size="lg"
                          style={{
                            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                            fontWeight: '500'
                          }}
                        >
                          <i className="fas fa-print me-2"></i>
                          <span className="d-none d-sm-inline">Print Receipt</span>
                          <span className="d-sm-none">Print</span>
                        </Button>
                      </div>
                    )}
                  
                  {/* Show "Try Again" button for failed payments (but not for invalid cart status) */}
                  {!isSuccess && !invalidCartStatus && (
                      <div className="col-12 col-md-6 col-lg-4">
                    <Button 
                      variant="warning" 
                      onClick={() => {
                        // Navigate to CheckoutPage with the correct parameters
                        logger.debug('[WipayResponsePage] Try Again clicked - transactionData:', transactionData);
                        logger.debug('[WipayResponsePage] supermarketId:', transactionData?.supermarketId);
                        logger.debug('[WipayResponsePage] cartInstanceId:', transactionData?.cartInstanceId);
                        
                        if (transactionData?.supermarketId && transactionData?.cartInstanceId) {
                          logger.debug('[WipayResponsePage] Navigating to checkout with params:', {
                            supermarketId: transactionData.supermarketId,
                            cartInstanceId: transactionData.cartInstanceId
                          });
                          navigate(`/checkout/${transactionData.supermarketId}/${transactionData.cartInstanceId}`);
                        } else {
                          // Fallback to carts page if we don't have the required data
                          logger.warn('[WipayResponsePage] Missing supermarketId or cartInstanceId for checkout navigation, falling back to carts page');
                          logger.warn('[WipayResponsePage] Available transactionData keys:', transactionData ? Object.keys(transactionData) : 'null');
                          navigate('/carts');
                        }
                      }} 
                          className="w-100 py-2 py-md-3"
                          size="lg"
                          style={{
                            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                            fontWeight: '500'
                          }}
                    >
                          <i className="fas fa-redo me-2"></i>
                          <span className="d-none d-sm-inline">Try Payment Again</span>
                          <span className="d-sm-none">Try Again</span>
                    </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Additional help text for failed payments */}
                  {!isSuccess && (
                    <div className="mt-3 text-center">
                      <small className="text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        If you continue to experience issues, please contact support or try a different payment method.
                      </small>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </Layout>
  );
}